import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import classNames from 'classnames';

import {
    isMobile,
    isWidget,
    useExperiment,
    getLanguageFromLocale,
    getBorderColourClassName,
    Sizes,
    Colours,
    AlternateAnchor,
    DefaultIntlFormatValues,
    EXPERIMENTS as BASE_UI_EXPERIMENTS,
    ANALYTICS_ATTRIBUTE_NAME,
    ANALYTICS_KEY,
    ExperimentSegments,
    getAffiliateDecoratedURL,
    Config,
} from '@ratehub/base-ui';

import EXPERIMENTS from '../definitions/Experiments';
import COMMON_MESSAGES from '../definitions/Messages';
import PRODUCT_TYPES from '../definitions/ProductTypes';
import FOCUS_VARIANTS from '../definitions/FocusVariants';
import CreditCardSynopsisShape from '../definitions/CreditCardSynopsisShape';
import isCIBCCreditCard from '../functions/isCIBCCreditCard';
import getEligibilityCheckerURL from '../functions/getEligibilityCheckerURL';
import extractRhAnalyticsFromCreditCard from '../functions/extractRhAnalyticsFromCreditCard';
import CreditCardCTA from './CreditCardCTA';
import CardLayoutBanner from './CardLayoutBanner';
import CardLayoutFooter from './CardLayoutFooter';
import CardLayoutTitleArea from './CardLayoutTitleArea';
import CardLayoutHighlights from './CardLayoutHighlights';
import ApprovalLikelihoodPill from './ApprovalLikelihoodPill';


function CreditCardLayout({
    cardInfo,
    productType,

    onClick,
    hasEligibilityCTA = false,
    borderColour,

    hasCategoryPill = false,
    hasBestUsedForPill = false,
    isCardFinder = false,
    isShowingApprovalLikelihood = false,

    focusVariant,
    isEqualizeHeight = false,
}) {
    const {
        giftCardOffer,
        cardImageURL,
        name,
        cardTypes,
        id: cardId,
        ratehubReview,
        isSponsored,
        isMonetized,
        displayType,
        bestUsedFor,
    } = cardInfo;

    const intl = useIntl();
    const language = getLanguageFromLocale(intl.locale);
    const isAWidget = isWidget();

    const mobileCTASegment = useExperiment(EXPERIMENTS.mobileCTA).segment;
    const shouldStackButtons = mobileCTASegment === ExperimentSegments.VARIANT_1;
    const shouldSeparateMobileButtons = Config.ENABLE_CC_CHECK_ELIGIBILITY_CTA
        && mobileCTASegment === ExperimentSegments.VARIANT_2
        && isMobile();

    const isDarkerOffers = useExperiment(BASE_UI_EXPERIMENTS.darkerOffers).segment === ExperimentSegments.VARIANT_1;
    const colourOfBorder = giftCardOffer && isDarkerOffers
        ? Colours.GRAPE_DARK
        : borderColour || Colours.STONE;

    const isCIBC = isCIBCCreditCard(cardInfo);

    const rightColumnWidth = getCardWidth({ isWide: isCardFinder });

    return (
        <Container
            className={classNames('rh-box-sizing-border-box rh-bg-coconut rh-border-style-solid rh-border-width-1px',
                getBorderColourClassName(colourOfBorder), ANALYTICS_ATTRIBUTE_NAME, {
                    'stack-buttons': shouldStackButtons,
                    'separate-buttons': shouldSeparateMobileButtons,
                })
            }
            ref={self => self && (self[ANALYTICS_KEY] = extractRhAnalyticsFromCreditCard(cardInfo))}
            borderRadius={BORDER_RADIUS}
            rightColumnWidth={rightColumnWidth}
            data-test-name={'credit-card'}
            data-test-product-type={productType}
        >
            <Choose>
                <When condition={giftCardOffer}>
                    <div className="rh-box-sizing-border-box">
                        <CardLayoutBanner
                            giftCardOffer={giftCardOffer}
                        />
                    </div>
                </When>
                <Otherwise>
                    {/* there is no giftCardOffer, but we may fill that space with a CardLayoutBanner placeholder */}
                    <If condition={isEqualizeHeight}>
                        <span className="rh-display-inline-block rh-icon-xs rh-my-0_25">
                            {' '}
                        </span>
                    </If>
                </Otherwise>
            </Choose>

            <div className="inner-body rh-display-flex rh-flex-wrap rh-px-0_5 rh-mb-1 rh-gap-0_5 rh-box-sizing-border-box">
                <div
                    className={classNames('row1 rh-display-flex rh-flex-wrap rh-box-sizing-border-box', {
                        'rh-mt-1': !isEqualizeHeight,
                        'rh-mt-0_25': isEqualizeHeight && giftCardOffer,
                    })}
                >
                    <div className="title-area rh-mx-0_5 rh-box-sizing-border-box">
                        <CardLayoutTitleArea
                            name={name}
                            ratehubReview={ratehubReview}
                            displayType={hasCategoryPill ? displayType : undefined}
                            bestUsedFor={hasBestUsedForPill ? bestUsedFor : undefined}
                            isSponsored={isSponsored && !isCardFinder}
                            isMonetized={isMonetized}

                            onClick={onClick}
                            isAWidget={isAWidget}
                            isEqualizeHeight={isEqualizeHeight}
                        />
                    </div>

                    <div className="card-image-wrapper rh-mx-0_25 rh-box-sizing-border-box">
                        <button
                            className="card-button rh-display-block rh-p-0 rh-m-auto rh-border-width-0"
                            data-name="card-image"
                            onClick={onClick}
                            type="button"
                        >
                            <img
                                src={cardImageURL}
                                alt={name}
                                width={rightColumnWidth}
                                height={getCardHeight({ isWide: isCardFinder })}
                            />
                        </button>
                    </div>
                </div>

                <div className="row2 rh-display-flex rh-flex-wrap-reverse rh-box-sizing-border-box">
                    <div className="body rh-mx-0_5 rh-box-sizing-border-box">
                        <CardLayoutHighlights
                            cardInfo={cardInfo}
                            productType={productType}
                            focusVariant={focusVariant}
                            isCIBC={isCIBC}
                            isEqualizeHeight={isEqualizeHeight}
                        />
                    </div>

                    <div className="cta-wrapper rh-mx-0_25 rh-mb-1_5 rh-box-sizing-border-box">
                        <If condition={isShowingApprovalLikelihood}>
                            <ApprovalLikelihoodPill
                                className="approval-likelihood-pill rh-mx-auto rh-mb-0_5 rh-box-sizing-border-box"
                                approvalLikelihood={cardInfo.approvalLikelihood}
                            />
                        </If>

                        <CreditCardCTA
                            cardTypes={cardTypes}
                            giftCardOffer={giftCardOffer}
                            cardId={cardId}

                            buttonSize="small"
                            hasEligibilityCTA={hasEligibilityCTA && !shouldSeparateMobileButtons}
                            onClick={onClick}
                        />
                    </div>
                </div>
            </div>

            <If condition={hasEligibilityCTA && shouldSeparateMobileButtons}>
                <div className="rh-mx-1">
                    <AlternateAnchor
                        className="lower-mobile-cta rh-display-block rh-mt-0 rh-mb-1 rh-mx-auto"
                        size="small"
                        href={getAffiliateDecoratedURL(getEligibilityCheckerURL(language, cardId, productType))}
                        target={isAWidget ? '_blank' : ''}
                        data-name="credit-cards.check-eligibility.mobile-cta-2"
                    >
                        <FormattedMessage
                            {...COMMON_MESSAGES.checkEligibility}
                            values={{
                                ...DefaultIntlFormatValues,
                            }}
                        />
                    </AlternateAnchor>
                </div>
            </If>

            <div
                className="rh-flex-grow-1 rh-flex-shrink-1 rh-width-100p rh-box-sizing-border-box"
                data-test-name="card-footer"
            >
                <CardLayoutFooter
                    cardInfo={cardInfo}
                    borderRadius={BORDER_RADIUS}
                    isAWidget={isAWidget}
                    isCIBC={isCIBC}
                />
            </div>
        </Container>
    );
}

CreditCardLayout.propTypes = {
    cardInfo: PropTypes.shape(CreditCardSynopsisShape).isRequired,
    productType: PropTypes.oneOf(Object.values(PRODUCT_TYPES)).isRequired,

    onClick: PropTypes.func,
    borderColour: PropTypes.oneOf(Object.values(Colours)),

    hasEligibilityCTA: PropTypes.bool,
    hasCategoryPill: PropTypes.bool,
    hasBestUsedForPill: PropTypes.bool,
    isCardFinder: PropTypes.bool,
    isShowingApprovalLikelihood: PropTypes.bool,

    focusVariant: PropTypes.oneOf(Object.values(FOCUS_VARIANTS)),
    isEqualizeHeight: PropTypes.bool,
};


// we need a wider second column to accommodate approval likelihood message (CardFinder only) and longer FR CTA
// increasing this width means long card names are more likely to wrap (noting that some card names will always wrap)
function getCardWidth({ isWide = false } = {}) {
    return isWide ? '192px' : '182px';
}

function getCardHeight({ isWide = false } = {}) {
    return parseInt(getCardWidth({ isWide })) * 0.625 + 'px';
}

const BUTTON_WIDTH_MAX = '240px';
const BORDER_RADIUS = '4px';


const Container = styled.div`
    ${({ borderRadius }) => `border-radius: ${borderRadius}`};

    box-shadow: 1px 0 2px 1px ${Colours.STONE_LIGHT},
        0 1px 2px 1px ${Colours.STONE_LIGHT},
        -1px 0 2px 1px ${Colours.STONE_LIGHT},
        0 -1px 2px 1px ${Colours.STONE_LIGHT};

    > .inner-body {
        > .row1,
        > .row2 {
            flex: 1 1 100%;
            gap: 0 ${Sizes.SPACING.QUARTER};
        }

        > .row1 > .title-area,
        > .row2 > .body {
            flex: 30 1 400px; // greedy title-area and body widths
        }

        > .row1 > .card-image-wrapper,
        > .row2 > .cta-wrapper {
            flex: 1 0 ${({ rightColumnWidth }) => `${rightColumnWidth}`};
        }

        > .row2 {
            > .cta-wrapper {
                > .approval-likelihood-pill {
                    max-width: ${({ rightColumnWidth }) => `${rightColumnWidth}`};
                }

                a,
                button {
                    // inappropriate familiarity: affects layout within CreditCardCTA
                    max-width: ${({ rightColumnWidth }) => `${rightColumnWidth}`};
                    flex: 1 1 150px;
                }
            }
        }
    }

    &.stack-buttons {
        > .inner-body {
            > .row2 {
                > .cta-wrapper {
                    a,
                    button {
                        // inappropriate familiarity: affects layout within CreditCardCTA
                        min-width: ${({ rightColumnWidth }) => `${rightColumnWidth}`};
                        max-width: ${BUTTON_WIDTH_MAX};
                    }
                }
            }
        }
    }

    &.separate-buttons {
        > .inner-body {
            > .row2 {
                > .cta-wrapper {
                    a,
                    button {
                        // inappropriate familiarity: affects layout within CreditCardCTA
                        min-width: ${({ rightColumnWidth }) => `${rightColumnWidth}`};
                        max-width: ${BUTTON_WIDTH_MAX};
                    }
                }
            }
        }

        .lower-mobile-cta {
            max-width: ${BUTTON_WIDTH_MAX};
        }
    }
`;


export default observer(CreditCardLayout);
